import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  setNotifications,
  removeNotification,
} from "../features/notifications/notificationSlice";
import { toast } from "react-toastify";
import { LanguageText } from "../dictionary/GroupNotificationText";

export const groupNotificationAPI = createApi({
  reducerPath: "groupNotificationAPI",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_URL,
    prepareHeaders: (headers: any, { getState }: any) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("token", `${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Notifications"],
  endpoints: (builder) => ({
    getNotifications: builder.query({
      query: (groupID) => {
        return {
          url: `/notifications/${groupID}`,
          method: "GET",
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setNotifications(data));
        } catch (error) {
          console.log("SOMETHING WENT WRONG FETCHING NOTIFICATIONS. TRY AGAIN LATER");
        }
      },
      providesTags: ["Notifications"],
    }),
    getNotificationByID: builder.query({
      query: (notificationID) => {
        return {
          url: `/notification/${notificationID}`,
          method: "GET",
        };
      },
      providesTags: ["Notifications"],
    }),
    createNotification: builder.mutation({
      query: (args: {
        notification: { [key: string]: any };
        language: LanguageText | null;
      }) => {
        return {
          url: "/notification/create",
          method: "POST",
          body: args.notification,
        };
      },
      invalidatesTags: ["Notifications"],
      async onQueryStarted(args, { dispatch }) {
        const Language = args.language;
        try {
          toast.success(Language?.createSuccess);
        } catch (error: any) {
          toast.error(Language?.errorMsg);
        }
      },
    }),
    updateNotification: builder.mutation({
      query: (args: {
        notification: { [key: string]: any };
        language: LanguageText | null;
      }) => {
        const { id: notificationId } = args.notification;
        return {
          url: `notification/${notificationId}/update`,
          method: "PUT",
          body: args.notification,
        };
      },
      invalidatesTags: ["Notifications"],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const Language = args.language;
        try {
          toast.success(Language?.updateSuccess);
        } catch (error: any) {
          toast.error(Language?.updateError);
        }
      },
    }),
    acknowledge: builder.mutation({
      query: (args: { notificationId: string; language: LanguageText | null }) => {
        const { notificationId } = args;
        return {
          url: `/notification/${notificationId}/ack`,
          method: "PUT",
        };
      },
      invalidatesTags: ["Notifications"],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const { language: Language } = args;
        try {
          const { meta } = await queryFulfilled;
          const statusCode = meta?.response?.status;
          statusCode === 200 && toast.success(Language?.acknowledgedSuccess);
        } catch (error) {
          toast.error(Language?.acknowledgedError);
        }
      },
    }),
    deleteNotification: builder.mutation({
      query: (args: { notificationID: string; language: LanguageText | null }) => {
        return {
          url: `notification/${args.notificationID}/delete`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Notifications"],
      async onQueryStarted(args, { dispatch }) {
        const Language = args.language;
        try {
          dispatch(removeNotification(args.notificationID));
          toast.success(Language?.deleteSuccess);
        } catch (error: any) {
          toast.error(Language?.deleteError);
        }
      },
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationByIDQuery,
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
  useAcknowledgeMutation,
  useDeleteNotificationMutation,
} = groupNotificationAPI;
